"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";

import {
  BreadcrumbBuffer,
  serializeBreadcrumbs,
  type Breadcrumb,
  type BreadcrumbType,
} from "@/lib/feedback/breadcrumbs";

type PushBreadcrumbInput = {
  type: BreadcrumbType;
  label: string;
  at?: number;
};

type BreadcrumbContextValue = {
  pushBreadcrumb: (entry: PushBreadcrumbInput) => void;
  listBreadcrumbs: () => Breadcrumb[];
  serializeBreadcrumbs: (maxBytes?: number) => Breadcrumb[];
  clearBreadcrumbs: () => void;
};

const fallbackBreadcrumbs: BreadcrumbContextValue = {
  pushBreadcrumb: () => undefined,
  listBreadcrumbs: () => [],
  serializeBreadcrumbs: () => [],
  clearBreadcrumbs: () => undefined,
};

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const bufferRef = useRef(new BreadcrumbBuffer(15));

  const pushBreadcrumb = useCallback((entry: PushBreadcrumbInput) => {
    const label = normalizeLabel(entry.label);
    if (!label) return;
    bufferRef.current.push({
      type: entry.type,
      label,
      at: entry.at ?? Date.now(),
    });
  }, []);

  useEffect(() => {
    if (!pathname) return;
    pushBreadcrumb({ type: "nav", label: pathname });
  }, [pathname, pushBreadcrumb]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      const label = readInteractionLabel(event.target);
      if (label) pushBreadcrumb({ type: "tap", label });
    }

    function onError(event: ErrorEvent) {
      pushBreadcrumb({ type: "error", label: messageFromErrorEvent(event) });
    }

    function onUnhandledRejection(event: PromiseRejectionEvent) {
      pushBreadcrumb({
        type: "error",
        label: messageFromUnknown(event.reason),
      });
    }

    document.addEventListener("click", onClick, { capture: true });
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [pushBreadcrumb]);

  const value = useMemo<BreadcrumbContextValue>(
    () => ({
      pushBreadcrumb,
      listBreadcrumbs: () => bufferRef.current.list(),
      serializeBreadcrumbs: (maxBytes?: number) =>
        serializeBreadcrumbs(bufferRef.current.list(), maxBytes),
      clearBreadcrumbs: () => bufferRef.current.clear(),
    }),
    [pushBreadcrumb],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs(): BreadcrumbContextValue {
  return useContext(BreadcrumbContext) ?? fallbackBreadcrumbs;
}

function readInteractionLabel(target: EventTarget | null): string {
  if (!(target instanceof Element)) return "";
  const el = target.closest<HTMLElement>("button, a, [role='button'], [data-bc]");
  if (!el) return "";
  return (
    el.dataset.bc ??
    el.getAttribute("aria-label") ??
    el.getAttribute("title") ??
    el.textContent ??
    ""
  );
}

function normalizeLabel(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 200);
}

function messageFromErrorEvent(event: ErrorEvent): string {
  if (event.error instanceof Error) {
    return `${event.error.name}: ${event.error.message}`;
  }
  return String(event.message || "Window error");
}

function messageFromUnknown(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
